<template>
  <div
    class="kot-date-picker"
    :class="{ 'kot-date-picker--compact': compact }"
    v-bind="$attrs"
  >
    <VueDatePicker
      :id="pickerId"
      :value="pickerValue"
      :placeholder="placeholder"
      :min-date="pickerMinDate"
      :max-date="pickerMaxDate"
      :format="format"
      :color="brandColor"
      :disabled="disabled"
      :clearable="clearable"
      :no-header="noHeader"
      :validate="effectiveValidate"
      :button-cancel="buttonCancel"
      :button-validate="buttonValidate"
      :locale="pickerLocale"
      :input-class="inputClass"
      @input="onInput"
    />
  </div>
</template>

<script>
import { VueDatePicker } from '@mathieustan/vue-datepicker';
import '@mathieustan/vue-datepicker/dist/vue-datepicker.min.css';
import { normalizePickerDate } from '@/utils/datehelper';
import { DEFAULT_THEME_PRIMARY } from '@/utils/kotTheme';

export default {
  name: 'DatePicker',
  inheritAttrs: false,
  components: {
    VueDatePicker,
  },
  props: {
    value: {
      type: [String, Date, Number],
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    minDate: {
      type: [String, Date, Number],
      default: '',
    },
    maxDate: {
      type: [String, Date, Number],
      default: '',
    },
    format: {
      type: String,
      default: 'DD/MM/YYYY',
    },
    id: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    noHeader: {
      type: Boolean,
      default: true,
    },
    validate: {
      type: Boolean,
      default: false,
    },
    buttonCancel: {
      type: String,
      default: 'Clear',
    },
    buttonValidate: {
      type: String,
      default: 'Today',
    },
    inputClass: {
      type: String,
      default: '',
    },
    compact: {
      type: Boolean,
      default: false,
    },
    /** Unused by library; kept for FilterBar compatibility */
    limit: {
      type: Array,
      default: null,
    },
  },
  computed: {
    /** Compact filters: apply date as soon as a day is clicked (no “Today” confirm step). */
    effectiveValidate() {
      if (this.compact) return false;
      return this.validate;
    },
    /** vue-datepicker/dayjs treat '' and null as invalid; undefined defaults to today */
    pickerValue() {
      return normalizePickerDate(this.value) || undefined;
    },
    pickerMinDate() {
      return normalizePickerDate(this.minDate) || undefined;
    },
    pickerMaxDate() {
      return normalizePickerDate(this.maxDate) || undefined;
    },
    pickerId() {
      return this.id || 'kot-datepicker';
    },
    brandColor() {
      if (typeof document === 'undefined') {
        return DEFAULT_THEME_PRIMARY;
      }
      const css = getComputedStyle(document.documentElement)
        .getPropertyValue('--brand-primary')
        .trim();
      return css || DEFAULT_THEME_PRIMARY;
    },
    pickerLocale() {
      return {
        lang: 'en',
        weekStart: 1,
      };
    },
  },
  methods: {
    onInput(date) {
      this.$emit('input', normalizePickerDate(date));
    },
  },
};
</script>

<style scoped>
.kot-date-picker {
  width: 100%;
}

.kot-date-picker ::v-deep .vd-wrapper {
  width: 100%;
}

.kot-date-picker ::v-deep .vd-picker__input {
  width: 100%;
  min-height: 36px;
}

.kot-date-picker ::v-deep .vd-picker__input input {
  width: 100% !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #1e293b !important;
}

.kot-date-picker--compact ::v-deep .vd-picker__input {
  min-height: 32px;
  height: 32px;
}

.kot-date-picker--compact ::v-deep .vd-picker__input input {
  height: 32px !important;
  min-height: 32px !important;
  min-width: 6.5em !important;
  padding: 0 2px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  line-height: 32px !important;
  box-sizing: border-box;
}
</style>
